import { create } from 'zustand';
import { createContext, useContext, ReactNode } from 'react';

export interface Tab {
  id: string;
  path: string;
  title: string;
  content: string;
}

interface TabStoreState {
  tabs: Tab[];
  activeTabId: string | null;
  openTab: (tab: Tab) => void;
  closeTab: (id: string) => void;
  setActiveTab: (id: string) => void;
  updateTabContent: (id: string, content: string) => void;
}

export const useTabStore = create<TabStoreState>((set) => ({
  tabs: [],
  activeTabId: null,
  openTab: (tab) => set((state) => {
    // If tab already open, just activate it
    if (state.tabs.some(t => t.path === tab.path)) {
      return { activeTabId: tab.id };
    }
    // Otherwise add new tab and activate it
    return {
      tabs: [...state.tabs, tab],
      activeTabId: tab.id
    };
  }),
  closeTab: (id) => set((state) => {
    const newTabs = state.tabs.filter(tab => tab.id !== id);
    let newActiveTabId = state.activeTabId;
    
    // If closing active tab, activate first tab
    if (id === state.activeTabId && newTabs.length > 0) {
      newActiveTabId = newTabs[0].id;
    } else if (newTabs.length === 0) {
      newActiveTabId = null;
    }
    
    return { tabs: newTabs, activeTabId: newActiveTabId };
  }),
  setActiveTab: (id) => set({ activeTabId: id }),
  updateTabContent: (id, content) => set((state) => ({
    tabs: state.tabs.map(tab =>
      tab.id === id ? { ...tab, content } : tab
    )
  })),
}));

export const TabStoreContext = createContext<TabStoreState | null>(null);

export const TabStoreProvider = ({ children }: { children: ReactNode }) => {
  const store = useTabStore();
  return (
    <TabStoreContext.Provider value={store}>
      {children}
    </TabStoreContext.Provider>
  );
};

export const useTabContext = () => {
  const context = useContext(TabStoreContext);
  if (!context) {
    throw new Error('useTabContext must be used within a TabStoreProvider');
  }
  return context;
};